import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  CountrySelect,
  LogSentryException,
  Modal,
  NumberInput,
  SegmentedControl,
  ServiceError,
  Spinner,
} from "@shapr3d/frontend-lib";
import classNames from "classnames";
import { Controller, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useBlocker, useSearchParams } from "react-router-dom";
import { z } from "zod";

import { ReactComponent as Icon_ApplePay24 } from "../../assets/images/ApplePay24.svg";
import { ReactComponent as Icon_CreditCardFilled24 } from "../../assets/images/CreditCardFilled24.svg";
import { chargebeeTrialDays, type InviteStorageData, sessionStorageKeys } from "../../config";
import useNavigation from "../../hooks/navigation";
import { TeamsUserRoles } from "../../hooks/useAccountTypes";
import {
  type CreateChargebeeCheckoutResponseData,
  EmptyCheckoutEstimateFormData,
  type EstimateResponseData,
  type StartCheckoutServiceErrorResponse,
  useInitialPeriod,
  useSubmitCheckout,
} from "../../hooks/useCheckout";
import { useEventListener } from "../../hooks/useEventListener";
import { useTracker } from "../../hooks/useTracker";
import useTranslation from "../../hooks/useTranslation";
import {
  APPLE_PAY_NOT_SUPPORTED,
  ApplePayButtonId,
  ChargebeePaymentType,
  useChargebee,
} from "../../lib/ChargebeeAdapter";
import { ElementClickAction, ModalShownAction } from "../../lib/TrackingAdapter";
import { SearchParam, ServiceErrorCode, SubscriptionPeriod, SubscriptionType } from "../../types";
import ChargebeeTrialTimeline from "../ChargebeeTrialTimeline/ChargebeeTrialTimeline";
import CreditCardInput from "../CreditCardInput/CreditCardInput";
import Heading from "../Heading/Heading";
import { Tiles } from "../Tiles/Tiles";
import { type CheckoutFormData, schema } from "./ChargebeeCheckoutSchema";
import BillingInfoForm from "./components/BillingInfoForm/BillingInfoForm";
import CheckoutFormProvider from "./components/CheckoutFormProvider";
import CheckoutSummary from "./components/CheckoutSummary/CheckoutSummary";
import { PriceSummary } from "./components/PriceSummary/PriceSummary";

import style from "./ChargebeeCheckout.module.css";

export const useWithInvite = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Clear invite emails if the user navigates away from the checkout page
  useBlocker(({ currentLocation, nextLocation }) => {
    if (currentLocation.pathname !== nextLocation.pathname) {
      sessionStorage.removeItem(sessionStorageKeys.inviteData);
    }
    return false;
  });

  useEffect(() => {
    const withInvite = searchParams.has(SearchParam.WithInvite);
    const inviteData = sessionStorage.getItem(sessionStorageKeys.inviteData);
    if (!withInvite || !inviteData) {
      sessionStorage.removeItem(sessionStorageKeys.inviteData);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete(SearchParam.WithInvite);
          return next;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  return searchParams.has(SearchParam.WithInvite);
};

const ChargebeeCheckout = ({
  initialCheckoutData,
}: {
  initialCheckoutData: CreateChargebeeCheckoutResponseData;
}) => {
  const { t, m } = useTranslation();
  const withInvite = useWithInvite();
  const initialPeriod = useInitialPeriod();

  const [submitError, setSubmitError] = useState<string | null>(null);

  const navigate = useNavigation();
  const tracker = useTracker();
  const isCheckoutSuccess = useRef(false);
  useEventListener("beforeunload", tracker.trackCheckoutAbandonment.bind(tracker));
  useEffect(() => {
    return () => {
      if (!isCheckoutSuccess.current) {
        tracker.trackCheckoutAbandonment();
      }
    };
  }, [tracker]);

  const { isLoading: isChargebeeLoading, isApplePaySupported } = useChargebee();

  const createCheckoutData = initialCheckoutData;

  const [searchParams] = useSearchParams();
  const showPeriodPickerParam = searchParams.get(SearchParam.CheckoutShowPeriodPicker) === "true";

  const showPeriodPicker = useMemo(
    () =>
      !createCheckoutData?.fixedPeriod &&
      (showPeriodPickerParam || initialPeriod === SubscriptionPeriod.Monthly),
    [createCheckoutData?.fixedPeriod, showPeriodPickerParam, initialPeriod]
  );

  useEffect(() => {
    tracker.trackCheckoutFlowLoaded("chargebee", Boolean(createCheckoutData?.trialEnd));
  }, [tracker, createCheckoutData]);

  const fromAddSeats = searchParams.has(SearchParam.FromAddSeats);
  const couponCodeFromURL = searchParams.get(SearchParam.SubscriptionCouponCode) ?? undefined;
  const isMigrationConsentRequired =
    createCheckoutData?.migratingFromType === SubscriptionType.AppStore &&
    createCheckoutData?.migrationAutoRenewEnabled;

  const inviteData = useMemo((): InviteStorageData | null => {
    const stored = sessionStorage.getItem(sessionStorageKeys.inviteData);
    return stored ? JSON.parse(stored) : null;
  }, []);

  const isContributorInvite = useMemo(() => {
    return inviteData?.role === TeamsUserRoles.Contributor;
  }, [inviteData]);

  const inviteEmails = inviteData?.emails ?? [];
  const inviteRole = inviteData?.role;
  const minSeats = fromAddSeats ? 2 : 1;
  const formRole =
    inviteRole === TeamsUserRoles.Editor || inviteRole === TeamsUserRoles.Contributor
      ? inviteRole
      : undefined;
  const editorSeatCount = isContributorInvite ? minSeats : inviteEmails.length + minSeats;

  const methods = useForm<CheckoutFormData, unknown, CheckoutFormData>({
    resolver: zodResolver(
      schema.superRefine((val, ctx) => {
        if (isMigrationConsentRequired && !val.migrationConsent) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Required",
            path: ["migrationConsent"],
          });
        }
      })
    ),
    mode: "onSubmit",
    defaultValues: {
      couponCode: couponCodeFromURL,
      ...EmptyCheckoutEstimateFormData,
      ...createCheckoutData.billingDetails,
      paymentType: ChargebeePaymentType.Card,
      period: createCheckoutData.fixedPeriod ?? initialPeriod,
      inviteEmails,
      role: formRole,
      editorSeatCount,
      migrationConsent: false,
    },
  });

  const {
    control,
    formState: { errors, isSubmitting },
    register,
    setError,
    setValue,
    reset: resetForm,
  } = methods;

  const {
    mutateAsync: submitCheckoutMutation,
    isPending: submitCheckoutIsPending,
    reset: resetSubmitCheckoutMutation,
  } = useSubmitCheckout();

  const countryCode = methods.watch("countryCode");
  const paymentType = methods.watch("paymentType");

  useEffect(() => {
    // TODO: send the specific invalid fields to telemetry
    if (errors && Object.keys(errors).length > 0) {
      tracker.trackCheckoutFlowIssue(
        Boolean(createCheckoutData?.trialEnd),
        "invalid_billing_fields"
      );
    }
  }, [errors, tracker, createCheckoutData]);

  const isLoading = submitCheckoutIsPending;

  const submit = useCallback(
    async (
      estimateResponseData: EstimateResponseData | undefined,
      checkoutID: string | undefined,
      formData: CheckoutFormData
    ) => {
      try {
        const isTrial = Boolean(estimateResponseData?.estimate.trialEnd);
        if (estimateResponseData && checkoutID) {
          await submitCheckoutMutation(
            {
              checkoutID,
              estimateResponseData,
              formData,
            },
            {
              onSuccess: () => {
                const additionalQueryParams: { [key in SearchParam]?: string } = {};
                isCheckoutSuccess.current = true;
                tracker.trackCheckoutFlowCompleted(
                  isTrial,
                  "chargebee",
                  formData.editorSeatCount,
                  paymentType
                );

                if (isTrial) {
                  toast.success(t(m.Checkout.Toast.TrialActivated));
                } else {
                  toast.success(t(m.Checkout.Toast.SubscriptionActivated));
                }

                if (withInvite) {
                  toast.success(t(m.Checkout.Toast.InvitationSent));
                  additionalQueryParams[SearchParam.WithInvite] = withInvite.toString();
                } else if (formData.editorSeatCount) {
                  toast.success(
                    t(m.Checkout.Toast.SeatsAdded, { count: formData.editorSeatCount })
                  );
                }

                navigate.goToPaymentSuccess({
                  additionalQueryParams,
                  omitQueryParams: [
                    SearchParam.CardError,
                    SearchParam.SubscriptionPeriod,
                    SearchParam.SubscriptionTier,
                  ],
                });
              },
              onError: (error) => {
                if (error.message === "CVC Declined" || error.message.includes("incorrect_cvc")) {
                  setError("card", {
                    message: t(m.Checkout.ValidationError.InvalidCvv),
                    type: "validate",
                  });
                }
                if (error.message === "Year is invalid") {
                  setError("card", {
                    message: t(m.Checkout.ValidationError.InvalidExpiration),
                    type: "validate",
                  });
                }

                if (
                  error.name === APPLE_PAY_NOT_SUPPORTED ||
                  error.message === APPLE_PAY_NOT_SUPPORTED
                ) {
                  tracker.trackApplePaySetupFailed({ location: "checkout" });
                  LogSentryException({
                    error: new Error("Apple Pay is not supported"),
                    level: "error",
                  });
                  setError("paymentType", {
                    message: t(m.Checkout.PaymentError.ApplePaySetupFailed),
                    type: "validate",
                  });
                  return;
                }

                if (
                  error instanceof ServiceError &&
                  error.hasCode(ServiceErrorCode.ValidationError)
                ) {
                  const err: ServiceError = error;
                  err.assertType<StartCheckoutServiceErrorResponse>();
                  tracker.trackCheckoutFlowIssue(isTrial, "invalid_billing_fields");

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.zip) {
                    setError("zip", {
                      message: t(m.Checkout.ValidationError.InvalidPostCode),
                      type: "validate",
                    });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.countryCode) {
                    LogSentryException({
                      error: new Error("Invalid country during checkout"),
                      level: "error",
                    });
                    setError("countryCode", {
                      message: t(m.Checkout.ValidationError.InvalidCountry),
                      type: "validate",
                    });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.stateCode) {
                    LogSentryException({
                      error: new Error("Invalid state during checkout"),
                      level: "error",
                    });
                    setError("stateCode", {
                      message: t(m.Checkout.ValidationError.InvalidState),
                      type: "validate",
                    });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.city) {
                    setError("city", {
                      message: t(m.Checkout.ValidationError.InvalidCity),
                      type: "validate",
                    });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.line1) {
                    setError("line1", {
                      message: t(m.Checkout.ValidationError.InvalidAddress),
                      type: "validate",
                    });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.line2) {
                    setError("line2", {
                      message: t(m.Checkout.ValidationError.InvalidAddress),
                      type: "validate",
                    });
                  }

                  if (err.serviceErrorResponse.validationErrors?.billingDetails?.vatNumber) {
                    setError("vatNumber", {
                      message: t(m.Checkout.ValidationError.InvalidVatNumber),
                      type: "validate",
                    });
                  }

                  return;
                }

                if (
                  error instanceof ServiceError &&
                  error.hasCode(ServiceErrorCode.InvalidCoupon)
                ) {
                  setSubmitError(ServiceErrorCode.InvalidCoupon);
                  return;
                }

                if (error instanceof Error) {
                  setSubmitError(error.message);
                  tracker.trackCheckoutFlowFailed({
                    trial: isTrial,
                    reason: "card_authorization_failed",
                    seats: formData.editorSeatCount ?? 0,
                    paymentPlatform: "chargebee",
                  });
                }
              },
            }
          );
        }
      } catch (error) {
        // This is to not throw Unhandled Promise Rejection error
      }
    },
    [submitCheckoutMutation, tracker, paymentType, withInvite, navigate, setError, t, m]
  );

  const onCreditCardValidationErrors = useCallback(() => {
    tracker.trackCheckoutFlowIssue(Boolean(createCheckoutData?.trialEnd), "card_validation_failed");
  }, [tracker, createCheckoutData]);

  const isApplePayModalOpen = paymentType === ChargebeePaymentType.ApplePay && isSubmitting;
  const hasTrackedApplePayModalModalOpen = useRef(false);
  useEffect(() => {
    if (isApplePayModalOpen && !hasTrackedApplePayModalModalOpen.current) {
      hasTrackedApplePayModalModalOpen.current = true;
      tracker.trackModalShown(ModalShownAction.APPLE_PAY_CONFIRMATION);
    }

    return () => {
      hasTrackedApplePayModalModalOpen.current = false;
    };
  }, [isApplePayModalOpen, tracker]);

  return (
    <CheckoutFormProvider
      methods={methods}
      submitError={submitError}
      checkout={createCheckoutData}
      submit={submit}
      isMigrationConsentRequired={isMigrationConsentRequired}
    >
      <div className={style.wrapper}>
        {isLoading && (
          <div className={style.loader}>
            <Spinner></Spinner>
            {submitCheckoutIsPending && paymentType === ChargebeePaymentType.Card && (
              <div className={style.loaderText}>{t(m.Checkout.ContactingCardIssuer)}</div>
            )}
          </div>
        )}
        <div className={classNames(style.pageWrapper, !isLoading && style.show)}>
          <div className={style.headerWrapper}>
            {createCheckoutData?.trialEnd ? (
              <>
                <Heading type="h2">
                  {t(m.Checkout.StartFreeTrial, { trialDays: chargebeeTrialDays })}
                </Heading>
                <ChargebeeTrialTimeline trialDays={chargebeeTrialDays} />
              </>
            ) : (
              <>
                <Heading type="h2">{t(m.Checkout.SubscribeNow)}</Heading>
                <div className={style.subtitle}>{t(m.Checkout.SubscribeNowSubtitle)}</div>
              </>
            )}
          </div>
          <div className={style.inlineCheckoutWrapper}>
            <div className={style.paymentDetails}>
              {!withInvite && (
                <div className={style.subgroup}>
                  <div>
                    <span className={style.checkoutLineText}>{t(m.Checkout.NumberOfSeats)} </span>
                    <span>{t(m.Checkout.IncludingYou)}</span>
                  </div>
                  <Controller
                    name="editorSeatCount"
                    render={({ field: { onChange, value } }) => {
                      const defaultSeats = fromAddSeats ? 2 : 1;
                      return (
                        <NumberInput
                          defaultValue={defaultSeats}
                          min={defaultSeats}
                          max={50}
                          onChange={(_, value) => {
                            tracker.trackButtonClick({
                              action: ElementClickAction.CHANGE_NUMBER_OF_SEATS,
                              details: `${value ?? defaultSeats}`,
                            });
                            onChange(value ?? defaultSeats);
                          }}
                          value={value ?? defaultSeats}
                        />
                      );
                    }}
                  />
                </div>
              )}
              {showPeriodPicker && (
                <div className={style.subgroup}>
                  <div className={style.checkoutLineText}>{t(m.Checkout.BillingPeriod)}</div>
                  <Controller
                    name="period"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <SegmentedControl
                        className={style.periodPicker}
                        checked={value === SubscriptionPeriod.Monthly}
                        onChange={(e) => {
                          const period = e.target.checked
                            ? SubscriptionPeriod.Monthly
                            : SubscriptionPeriod.Yearly;
                          onChange(period);
                          tracker.trackButtonClick({
                            action: ElementClickAction.CHANGE_BILLING_PERIOD,
                            details: period,
                          });
                        }}
                        optionA={t(m.Checkout.Yearly)}
                        optionASecondaryText={t(m.Checkout.YearlySave)}
                        optionB={t(m.Checkout.Monthly)}
                      />
                    )}
                  />
                </div>
              )}

              {isApplePaySupported && (
                <div className={style.subgroup}>
                  <div className={style.checkoutLineText}>{t(m.Checkout.PaymentMethod)}</div>
                  <Tiles
                    registerAttrs={register("paymentType")}
                    options={[
                      {
                        value: ChargebeePaymentType.ApplePay,
                        label: t(m.Checkout.ApplePay),
                        icon: <Icon_ApplePay24 />,
                      },
                      {
                        value: ChargebeePaymentType.Card,
                        label: t(m.Checkout.CreditDebitCard),
                        icon: <Icon_CreditCardFilled24 />,
                      },
                    ]}
                  />
                  {errors?.paymentType && (
                    <div className={style.errorMessage}>{errors.paymentType.message}</div>
                  )}
                </div>
              )}

              {!isChargebeeLoading && (
                <div
                  className={classNames(
                    style.subgroup,
                    paymentType !== ChargebeePaymentType.Card && style.hidden
                  )}
                >
                  <div className={style.checkoutLineText}>{t(m.Checkout.CreditCardDetails)}</div>
                  <CreditCardInput
                    name="card"
                    control={control}
                    onValidationErrors={onCreditCardValidationErrors}
                  />
                  <div className={style.creditCardLocationContainer}>
                    <CountrySelect
                      errorMessage={errors.countryCode?.message}
                      required
                      value={countryCode}
                      onChange={(e) => {
                        setValue("countryCode", e.target.value);
                      }}
                    />
                  </div>
                </div>
              )}

              <BillingInfoForm />
            </div>
            <CheckoutSummary />
          </div>
        </div>
        <Modal
          title={t(m.Checkout.ApplePay)}
          open={isApplePayModalOpen}
          onClose={() => {
            resetSubmitCheckoutMutation();
            resetForm(undefined, { keepDirtyValues: true });
          }}
        >
          <div className={style.applePayCheckoutSummaryContainer}>
            <PriceSummary />
          </div>
          <div className={style.applePayButtonContainer}>
            <Button className={style.applePayButtonPlaceholder} loading />
            <div
              id={ApplePayButtonId}
              onClick={() => {
                tracker.trackApplePayButtonClicked({ location: "checkout" });
              }}
            />
          </div>
        </Modal>
      </div>
    </CheckoutFormProvider>
  );
};

export default ChargebeeCheckout;
